import { useEffect, useState } from "react";
import { Plus, Edit, Trash2 } from "lucide-react";
import Sidebar from "../components/StudentSidebar";
import '../styles/AdminMessages.css';
import EditUserMessageModal from "../components/EditUserMessageModal";

export default function StudentMessages() {
  // Systemowe wiadomości
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);

  // User message modal state
  const [showUserMessageModal, setShowUserMessageModal] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [userMessageUsers, setUserMessageUsers] = useState([]);
  const [userMessageSubject, setUserMessageSubject] = useState('');
  const [userMessageContent, setUserMessageContent] = useState('');
  const [userMessageType, setUserMessageType] = useState('info');
  const [userMessageSearchTerm, setUserMessageSearchTerm] = useState('');

  useEffect(() => {
    const token = localStorage.getItem("token");
    
    // Get current user ID from token
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserId(payload.id);
      } catch (error) {
        // Error parsing token
      }
    }

    async function fetchSystemMessages() {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/messages/student`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          setMessages(data);
        } else {
          setMessages([]);
        }
      } catch (error) {
        setMessages([]);
      } finally {
        setLoading(false);
      }
    }

    fetchSystemMessages();
  }, []);

  // Fetch users when modal opens - only admins and lecturers
  useEffect(() => {
    if (showUserMessageModal) {
      const fetchUsers = async () => {
        try {
          const token = localStorage.getItem('token');
          if (!token) {
            return;
          }
          const response = await fetch(`${process.env.REACT_APP_API_URL}/mail/users`, {
            headers: { Authorization: `Bearer ${token}` },
            credentials: 'include'
          });
          if (response.ok) {
            const data = await response.json();
            // Filter to only show admins and lecturers (not students)
            const filteredUsers = data.filter(user => 
              user.user_type === 'administrator' || user.user_type === 'lecturer'
            );
            setAllUsers(filteredUsers);
          } else {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
            alert('Błąd podczas pobierania listy użytkowników: ' + (errorData.error || `Status: ${response.status}`));
          }
        } catch (error) {
          alert('Błąd podczas pobierania listy użytkowników: ' + error.message);
        }
      };
      fetchUsers();
    }
  }, [showUserMessageModal]);

  const handleMessageUpdated = () => {
    const token = localStorage.getItem("token");
    fetch(`${process.env.REACT_APP_API_URL}/messages/student`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => setMessages(data))
      .catch(err => {
        // Error fetching messages
      });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Czy na pewno chcesz usunąć tę wiadomość?")) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/messages/${id}`,
        { 
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (res.ok) handleMessageUpdated();
    } catch (err) {
      // Error deleting message
    }
  };

  const handleDeleteUserMessage = async (id) => {
    if (!window.confirm("Czy na pewno chcesz usunąć wiadomość od użytkownika?")) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/messages/user/${id}`,
        { 
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (res.ok) {
        // Refresh messages to update the list
        handleMessageUpdated();
      }
    } catch (err) {
      // Error deleting user message
    }
  };

  return (
    <div className="admin-dashboard">
      {/* Sidebar */}
      <Sidebar />

      {/* Main content */}
      <div className="main-content">
        <div className="header">
          <div>
            <h1 className="header-title">Wiadomości systemowe</h1>
            <p className="header-subtitle">
              Wiadomości systemowe oraz wiadomości od użytkowników
            </p>
          </div>
        </div>

        {/* Zawartość strony */}
        <div className="lecturer-messages-content">
          {/* KARTA 1 – WIADOMOŚCI GLOBALNE I GRUPOWE */}
          <div className="management-card">
            <div className="card-header-management">
              <h3>Wiadomości globalne i grupowe</h3>
            </div>

            {loading ? (
              <p>Ładowanie wiadomości...</p>
            ) : (
              <table className="message-table">
                <thead>
                  <tr>
                    <th>Tytuł</th>
                    <th>Treść</th>
                    <th>Typ</th>
                    <th>Odbiorcy</th>
                    <th>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const filtered = messages.filter(msg => {
                      // Show ALL global/group messages (these are broadcast/received messages)
                      const userCount = parseInt(msg.user_count) || 0;
                      const hasGroups = msg.groups && msg.groups.trim && msg.groups.trim().length > 0;
                      const isGlobalOrGroup = msg.is_global === true || (hasGroups && userCount === 0);
                      return isGlobalOrGroup;
                    });
                    return filtered.length > 0 ? (
                      filtered.map((msg) => (
                      <tr key={msg.message_id}>
                        <td>{msg.title}</td>
                        <td>{msg.content}</td>
                        <td>
                          <span
                            className={`badge badge-${msg.message_type}`}
                          >
                            {msg.message_type === "info"
                              ? "Informacja"
                              : msg.message_type === "warning"
                              ? "Ważne"
                              : "Alert"}
                          </span>
                        </td>
                        <td>
                          {msg.is_global ? "Wszyscy" : msg.groups || msg.group_code || "-"}
                        </td>
                        <td>
                          {new Date(msg.created_at).toLocaleString("pl-PL", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                      </tr>
                    ))
                    ) : (
                      <tr>
                        <td colSpan="5">Brak wiadomości</td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            )}
          </div>

          {/* KARTA 2 – POWIADOMIENIA DO UŻYTKOWNIKÓW */}
          <div className="management-card">
            <div className="card-header-management">
              <h3>Wiadomości wewnętrzne do użytkowników</h3>
              <div className="button-container">
                <button
                  className="btn btn-primary"
                  onClick={() => setShowUserMessageModal(true)}
                  type="button"
                >
                  <Plus size={16} className="icon-style" />
                  Wyślij do użytkowników
                </button>
              </div>
            </div>

            {loading ? (
              <p>Ładowanie wiadomości...</p>
            ) : (
              <table className="message-table">
                <thead>
                  <tr>
                    <th>Tytuł</th>
                    <th>Treść</th>
                    <th>Typ</th>
                    <th>Odbiorcy</th>
                    <th>Data</th>
                    <th>Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const filtered = messages.filter(msg => {
                      const userCount = parseInt(msg.user_count) || 0;
                      const msgCreatedBy = parseInt(msg.created_by) || null;
                      // Show ONLY messages sent BY student to users
                      return currentUserId && msgCreatedBy === currentUserId && userCount > 0;
                    });
                    return filtered.length > 0 ? (
                      filtered.map((msg) => (
                      <tr key={msg.message_id}>
                        <td>{msg.title}</td>
                        <td>{msg.content}</td>
                        <td>
                          <span
                            className={`badge badge-${msg.message_type}`}
                          >
                            {msg.message_type === "info"
                              ? "Informacja"
                              : msg.message_type === "warning"
                              ? "Ważne"
                              : "Alert"}
                          </span>
                        </td>
                        <td>
                          {msg.recipient_users && msg.recipient_users.length > 0 ? (
                            <div style={{ fontSize: '0.875rem' }}>
                              {msg.recipient_users.length === 1 
                                ? msg.recipient_users[0].full_name || msg.recipient_users[0].username
                                : `${msg.recipient_users.length} użytkowników`}
                              {msg.recipient_users.length <= 3 && (
                                <div style={{ marginTop: '4px', fontSize: '0.75rem', color: '#64748b' }}>
                                  {msg.recipient_users.map(u => u.full_name || u.username).join(', ')}
                                </div>
                              )}
                            </div>
                          ) : (
                            `${msg.user_count || 0} użytkowników`
                          )}
                        </td>
                        <td>
                          {new Date(msg.created_at).toLocaleString("pl-PL", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                        <td className="message-actions">
                          <button
                            className="btn-icon edit"
                            onClick={() => {
                              setSelectedMessage(msg);
                              setIsEditUserModalOpen(true);
                            }}
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            className="btn-icon delete"
                            onClick={() => handleDelete(msg.message_id)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                    ) : (
                      <tr>
                        <td colSpan="6">Brak wiadomości do wyświetlenia.</td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            )}
          </div>

          {/* KARTA 3 – POWIADOMIENIA OD WYKŁADOWCÓW I ADMINISTRATORÓW */}
          <div className="management-card">
            <div className="card-header-management">
              <h3>Wiadomości wewnętrzne od zarządzajacych oraz wykładowców</h3>
            </div>

            {loading ? (
              <p>Ładowanie wiadomości od zarządzajacych oraz wykładowców...</p>
            ) : (
              <table className="message-table">
                <thead>
                  <tr>
                    <th>Tytuł</th>
                    <th>Treść</th>
                    <th>Typ</th>
                    <th>Nadawca</th>
                    <th>Kiedy wysłana</th>
                    <th>Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Get messages sent TO student (where student is recipient but NOT sender)
                    // Use a Set to track message IDs and prevent duplicates
                    const seenMessageIds = new Set();
                    const messagesToStudent = messages.filter(msg => {
                      const userCount = parseInt(msg.user_count) || 0;
                      if (userCount === 0) return false;
                      // Show messages sent TO student (where student is recipient but NOT sender)
                      const sentToMe = currentUserId && 
                        msg.recipient_users && 
                        msg.recipient_users.some(u => parseInt(u.user_id) === currentUserId) &&
                        parseInt(msg.created_by) !== currentUserId;
                      
                      // Prevent duplicates
                      if (sentToMe && !seenMessageIds.has(msg.message_id)) {
                        seenMessageIds.add(msg.message_id);
                        return true;
                      }
                      return false;
                    });
                    
                    return messagesToStudent.length > 0 ? (
                      messagesToStudent.map((msg) => (
                        <tr key={msg.message_id}>
                          <td>{msg.title}</td>
                          <td>{msg.content}</td>
                          <td>
                            <span className={`badge badge-${msg.message_type || 'info'}`}>
                              {msg.message_type === "info"
                                ? "Informacja"
                                : msg.message_type === "warning"
                                ? "Ważne"
                                : msg.message_type === "alert"
                                ? "Alert"
                                : msg.message_type || "Informacja"}
                            </span>
                          </td>
                          <td>
                            {msg.created_by_full_name || msg.created_by_username || "-"}
                          </td>
                          <td>
                            {msg.created_at
                              ? new Date(msg.created_at).toLocaleString("pl-PL", {
                                  year: "numeric",
                                  month: "2-digit",
                                  day: "2-digit",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : "-"}
                          </td>
                          <td className="message-actions">
                            <button
                              className="btn-icon delete"
                              onClick={() => handleDeleteUserMessage(msg.message_id)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6">
                          Brak wiadomości od użytkowników do wyświetlenia.
                        </td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* User Message Modal - Send message to specific users (only admins/lecturers) */}
        {showUserMessageModal && (
          <div className='user-selector-modal-overlay' onClick={() => setShowUserMessageModal(false)}>
            <div className='user-message-modal' onClick={(e) => e.stopPropagation()}>
              <div className='user-selector-header'>
                <h3>Wyślij wiadomość wewnętrzną do użytkowników</h3>
                <button 
                  className='user-selector-close'
                  onClick={() => {
                    setShowUserMessageModal(false);
                    setUserMessageUsers([]);
                    setUserMessageSubject('');
                    setUserMessageContent('');
                    setUserMessageType('info');
                    setUserMessageSearchTerm('');
                  }}
                >
                  ×
                </button>
              </div>

              <div className='user-message-modal-content'>
                {/* Message Type */}
                <div className='user-message-section'>
                  <label className='form-label'>Typ wiadomości</label>
                  <select
                    className='form-input'
                    value={userMessageType}
                    onChange={(e) => setUserMessageType(e.target.value)}
                  >
                    <option value='info'>Informacja</option>
                    <option value='warning'>Ważne</option>
                    <option value='alert'>Alert</option>
                  </select>
                </div>

                {/* Message Subject */}
                <div className='user-message-section'>
                  <label className='form-label'>Temat wiadomości</label>
                  <input 
                    type='text'
                    className='form-input'
                    placeholder='Np. Ważna informacja'
                    value={userMessageSubject}
                    onChange={(e) => setUserMessageSubject(e.target.value)}
                  />
                </div>

                {/* Message Content */}
                <div className='user-message-section'>
                  <label className='form-label'>Treść</label>
                  <textarea 
                    className='form-textarea'
                    placeholder='Treść wiadomości...'
                    value={userMessageContent}
                    onChange={(e) => setUserMessageContent(e.target.value)}
                    rows={6}
                  />
                </div>

                {/* User Selection */}
                <div className='user-message-section'>
                  <label className='form-label'>Odbiorcy (tylko administratorzy i wykładowcy)</label>
                  <div className='user-selector-search'>
                    <input
                      type='text'
                      placeholder='Szukaj użytkownika...'
                      value={userMessageSearchTerm}
                      onChange={(e) => setUserMessageSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className='user-selector-list' style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {allUsers.length === 0 ? (
                      <div style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>
                        Ładowanie użytkowników...
                      </div>
                    ) : (() => {
                      const filteredUsers = allUsers.filter(user => {
                        const searchLower = userMessageSearchTerm.toLowerCase();
                        return !userMessageSearchTerm || 
                          user.full_name?.toLowerCase().includes(searchLower) ||
                          user.username?.toLowerCase().includes(searchLower) ||
                          user.email?.toLowerCase().includes(searchLower);
                      });
                      return filteredUsers.length === 0 ? (
                        <div style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>
                          Brak użytkowników spełniających kryteria wyszukiwania
                        </div>
                      ) : (
                        filteredUsers.map(user => (
                          <label key={user.user_id} className='user-selector-item'>
                            <input
                              type='checkbox'
                              checked={userMessageUsers.includes(user.user_id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setUserMessageUsers([...userMessageUsers, user.user_id]);
                                } else {
                                  setUserMessageUsers(userMessageUsers.filter(id => id !== user.user_id));
                                }
                              }}
                            />
                            <div className='user-selector-item-info'>
                              <div className='user-selector-item-name'>{user.full_name || user.username}</div>
                              <div className='user-selector-item-details'>
                                {user.email && <span>{user.email}</span>}
                                <span className={`user-type-badge user-type-${user.user_type}`}>
                                  {user.user_type === 'student' ? 'Kursant' : 
                                   user.user_type === 'lecturer' ? 'Wykładowca' : 
                                   user.user_type === 'administrator' ? 'Administrator' : user.user_type}
                                </span>
                                {user.group_name && <span>Grupa: {user.group_name}</span>}
                              </div>
                            </div>
                          </label>
                        ))
                      );
                    })()}
                  </div>
                  {userMessageUsers.length > 0 && (
                    <div style={{ marginTop: '10px', fontSize: '0.9em', color: '#666', padding: '0.5rem', background: '#f8fafc', borderRadius: '6px' }}>
                      Wybrano: {userMessageUsers.length} {userMessageUsers.length === 1 ? 'użytkownika' : 'użytkowników'}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className='user-selector-footer'>
                <button 
                  className='btn btn-secondary'
                  onClick={() => {
                    setShowUserMessageModal(false);
                    setUserMessageUsers([]);
                    setUserMessageSubject('');
                    setUserMessageContent('');
                    setUserMessageType('info');
                    setUserMessageSearchTerm('');
                  }}
                >
                  Anuluj
                </button>
                <button 
                  className='btn btn-primary'
                  onClick={async () => {
                    if (!userMessageSubject.trim() || !userMessageContent.trim()) {
                      alert('Proszę wypełnić temat i treść wiadomości.');
                      return;
                    }
                    if (userMessageUsers.length === 0) {
                      alert('Proszę wybrać przynajmniej jednego użytkownika.');
                      return;
                    }

                    try {
                      const token = localStorage.getItem('token');
                      const headers = { 'Content-Type': 'application/json' };
                      if (token) {
                        headers.Authorization = `Bearer ${token}`;
                      }

                      const payload = {
                        subject: userMessageSubject,
                        content: userMessageContent,
                        messageType: 'notification',
                        message_type: userMessageType,
                        selectedUsers: userMessageUsers,
                        sendOptions: {}
                      };

                      const response = await fetch(`${process.env.REACT_APP_API_URL}/mail/send`, {
                        method: 'POST',
                        headers: headers,
                        credentials: 'include',
                        body: JSON.stringify(payload)
                      });

                      if (response.ok) {
                        const data = await response.json();
                        alert(`Wiadomość została wysłana pomyślnie do ${data.recipientsCount} odbiorców!`);
                        setShowUserMessageModal(false);
                        setUserMessageUsers([]);
                        setUserMessageSubject('');
                        setUserMessageContent('');
                        setUserMessageType('info');
                        setUserMessageSearchTerm('');
                        handleMessageUpdated();
                      } else {
                        const errorData = await response.json();
                        alert(errorData.error || 'Błąd podczas wysyłania wiadomości.');
                      }
                    } catch (error) {
                      alert('Błąd podczas wysyłania wiadomości.');
                    }
                  }}
                  disabled={userMessageUsers.length === 0}
                >
                  Wyślij ({userMessageUsers.length})
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Message Modal */}
        {selectedMessage && (
          <EditUserMessageModal
            isOpen={isEditUserModalOpen}
            onClose={() => {
              setIsEditUserModalOpen(false);
              setSelectedMessage(null);
            }}
            message={selectedMessage}
            onMessageUpdated={handleMessageUpdated}
          />
        )}
      </div>
    </div>
  );
}
